import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import {
  FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiX
} from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import APIGateway, {
  type ActionLogResponse,
  type LogFilters,
  type ModelLogFilters,
  type ModelLogResponse,
  type RequestLogResponse
} from '@/api';
import { AgentTraceViewer, type TraceUrlState } from '@/app/agents/details/traceViewer/traceViewer';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import ActionLogViewer from '@/components/logs/LogViewers/actionLogViewer';
import ModelLogViewer from '@/components/logs/LogViewers/modelLogViewer';
import RequestResponseLogViewer from '@/components/logs/LogViewers/requestResponseLogViewer';
import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { SortOrder } from '@/types/logs';

import styles from './page.module.scss';


type LogType = 'modelLog' | 'actionLog' | 'requestResponseLog' | 'trace';

type LogData =
  | { type: 'none'; data: Record<string, never> }
  | { type: 'actionLog'; data: ActionLogResponse }
  | { type: 'modelLog'; data: ModelLogResponse }
  | { type: 'requestResponseLog'; data: RequestLogResponse };

const LOG_TYPE_LABELS: Record<LogType, string> = {
  modelLog: 'Model Log',
  actionLog: 'Action Log',
  requestResponseLog: 'Request/Response',
  trace: 'Traces'
};

/** Sort field options per log type. Matches columns the backend can order by. */
const SORT_BY_OPTIONS: Record<LogType, { value: string; label: string }[]> = {
  modelLog: [
    { value: 'end_time', label: 'End time' },
    { value: 'start_time', label: 'Start time' }
  ],
  actionLog: [
    { value: 'end_time', label: 'End time' },
    { value: 'start_time', label: 'Start time' }
  ],
  requestResponseLog: [
    { value: 'start_time', label: 'Start time' },
    { value: 'end_time', label: 'End time' }
  ],
  trace: [
    { value: 'start_time', label: 'Start time' },
    { value: 'end_time', label: 'End time' }
  ]
};

const SORT_ORDER_LABELS: Record<SortOrder, string> = {
  DESC: 'Newest first',
  ASC: 'Oldest first'
};

function isLogType( value: string | null | undefined ): value is LogType {
  return !!value && Object.hasOwn( LOG_TYPE_LABELS, value );
}

function isSortOrder( value: string | null | undefined ): value is SortOrder {
  return value === 'ASC' || value === 'DESC';
}

/** Build a URLSearchParams from explicit values, omitting empty/default ones. */
function buildParams( values: Record<string, string | number | undefined> ): URLSearchParams {
  const params = new URLSearchParams();
  for ( const [ key, value ] of Object.entries( values ) ) {
    if ( value !== undefined && value !== '' ) {
      params.set( key, String( value ) );
    }
  }
  return params;
}

/** Convert a datetime-local input value (yyyy-mm-ddThh:mm) into an ISO timestamp, or undefined. */
function dateInputToIso( value: string ): string | undefined {
  if ( !value ) return undefined;
  const dt = DateTime.fromISO( value );
  if ( !dt.isValid ) return undefined;
  return dt.toISO() ?? undefined;
}

const DEFAULT_SORT_ORDER: SortOrder = 'DESC';

export default function LogsPage() {
  const [ searchParams ] = useSearchParams();

  const [ useLocalZone ] = useLocalZonePreference();

  // ── Filter state (initialized from URL on first mount) ────────────────────
  const [ agentId, setAgentId ] = useState<string>( () => searchParams.get( 'agentId' ) ?? '' );
  const [ agentName, setAgentName ] = useState<string>( () => searchParams.get( 'agentName' ) ?? '' );
  const [ constellationName, setConstellationName ] = useState<string>( () => searchParams.get( 'constellationName' ) ?? '' );
  const [ constellationVersion, setConstellationVersion ] = useState<string>( () => searchParams.get( 'constellationVersion' ) ?? '' );
  const [ clientId, setClientId ] = useState<string>( () => searchParams.get( 'clientId' ) ?? '' );
  const [ modelId, setModelId ] = useState<string>( () => searchParams.get( 'modelId' ) ?? '' );
  const [ correlationId, setCorrelationId ] = useState<string>( () => searchParams.get( 'correlationId' ) ?? '' );
  const [ requestId, setRequestId ] = useState<string>( () => searchParams.get( 'requestId' ) ?? '' );
  const [ sessionIdFilter, setSessionIdFilter ] = useState<string>( () => searchParams.get( 'sessionIdFilter' ) ?? '' );
  const [ dateFrom, setDateFrom ] = useState<string>( () => searchParams.get( 'dateFrom' ) ?? '' );
  const [ dateTo, setDateTo ] = useState<string>( () => searchParams.get( 'dateTo' ) ?? '' );
  const [ sortOrder, setSortOrder ] = useState<SortOrder>( () => {
    const p = searchParams.get( 'sortOrder' );
    return isSortOrder( p ) ? p : DEFAULT_SORT_ORDER;
  } );
  const [ sortBy, setSortBy ] = useState<string>( () => searchParams.get( 'sortBy' ) ?? '' );

  const [ currentPage, setCurrentPage ] = useState<number>( () => parseInt( searchParams.get( 'page' ) ?? '1', 10 ) || 1 );
  const [ logType, setLogType ] = useState<LogType>( () => {
    const p = searchParams.get( 'logType' );
    return isLogType( p ) ? p : 'modelLog';
  } );
  const [ filtersOpen, setFiltersOpen ] = useState<boolean>( () => searchParams.get( 'filtersOpen' ) === '1' );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ logData, setLogData ] = useState<LogData>( { type: 'none', data: {} } );
  // Pagination cursor returned by the server; chained for subsequent pages.
  const [ sessionId, setSessionId ] = useState<string>( '' );
  const [ hasMorePages, setHasMorePages ] = useState<boolean>( false );
  const [ selectedId, setSelectedId ] = useState<string>( () => searchParams.get( 'selectedId' ) ?? '' );

  // Trace-only state that the AgentTraceViewer owns (keyword, pagination, selection).
  // Filter values come from /logs state and are passed in via buildTraceInitialState().
  const traceExtraStateRef = useRef( {
    traceKeyword: searchParams.get( 'traceKeyword' ) ?? '',
    traceOffset: parseInt( searchParams.get( 'traceOffset' ) ?? '0', 10 ) || 0,
    selectedRequest: searchParams.get( 'selectedRequest' ) ?? ''
  } );
  // Bump to force remount of AgentTraceViewer when the user clicks Search in trace mode.
  const [ traceMountKey, setTraceMountKey ] = useState( 0 );

  // ── URL sync ──────────────────────────────────────────────────────────────
  const stateRef = useRef( {
    agentId, agentName, constellationName, constellationVersion, clientId, modelId,
    correlationId, requestId, sessionIdFilter, dateFrom, dateTo, sortOrder, sortBy,
    logType, currentPage, selectedId, filtersOpen
  } );
  stateRef.current = {
    agentId, agentName, constellationName, constellationVersion, clientId, modelId,
    correlationId, requestId, sessionIdFilter, dateFrom, dateTo, sortOrder, sortBy,
    logType, currentPage, selectedId, filtersOpen
  };

  /** Write URL from the latest state ref + optional overrides. */
  const writeUrl = ( overrides: Partial<typeof stateRef.current> = {} ) => {
    const s = { ...stateRef.current, ...overrides };
    // Filter params are shared between trace and non-trace modes.
    const filterParams = {
      logType: s.logType,
      agentId: s.agentId || undefined,
      agentName: s.agentName || undefined,
      constellationName: s.constellationName || undefined,
      constellationVersion: s.constellationVersion || undefined,
      clientId: s.clientId || undefined,
      modelId: s.logType === 'modelLog' ? ( s.modelId || undefined ) : undefined,
      correlationId: s.correlationId || undefined,
      requestId: s.requestId || undefined,
      sessionIdFilter: s.sessionIdFilter || undefined,
      dateFrom: s.dateFrom || undefined,
      dateTo: s.dateTo || undefined,
      sortOrder: s.sortOrder !== DEFAULT_SORT_ORDER ? s.sortOrder : undefined,
      sortBy: s.sortBy || undefined,
      filtersOpen: s.filtersOpen ? '1' : undefined
    };
    const params = s.logType === 'trace'
      ? buildParams( {
          ...filterParams,
          traceKeyword: traceExtraStateRef.current.traceKeyword || undefined,
          traceOffset: traceExtraStateRef.current.traceOffset > 0
            ? traceExtraStateRef.current.traceOffset
            : undefined,
          selectedRequest: traceExtraStateRef.current.selectedRequest || undefined
        } )
      : buildParams( {
          ...filterParams,
          page: s.currentPage > 1 ? s.currentPage : undefined,
          selectedId: s.selectedId || undefined
        } );
    const url = new URL( window.location.href );
    url.search = params.toString();
    window.history.replaceState( null, '', url.toString() );
  };

  // ── Auto-fetch on navigation and log-type change ──────────────────────────
  // Each non-trace section loads its most recent logs as soon as it's shown,
  // using whatever filters are in state (none by default, or those restored
  // from a shared link). Traces auto-load via the AgentTraceViewer itself.
  useEffect( () => {
    if ( logType === 'trace' ) return;
    fetchLogData( currentPage );
  }, [ logType ] );

  const handleLogTypeChange = ( type: LogType ) => {
    setLogType( type );
    setSessionId( '' );
    setSelectedId( '' );
    setCurrentPage( 1 );
    setLogData( { type: 'none', data: {} } );
    // The effect above re-fetches for the new log type once state settles.
    writeUrl( { logType: type, selectedId: '', currentPage: 1 } );
  };

  const handleSearch = () => {
    if ( logType === 'trace' ) {
      // Re-mount the trace viewer with the current filter values, resetting
      // selection + pagination since the result set changes.
      traceExtraStateRef.current = {
        ...traceExtraStateRef.current,
        traceOffset: 0,
        selectedRequest: ''
      };
      setTraceMountKey( k => k + 1 );
      writeUrl();
      return;
    }
    setCurrentPage( 1 );
    setSessionId( '' );
    setSelectedId( '' );
    writeUrl( { currentPage: 1, selectedId: '' } );
    fetchLogData( 1 );
  };

  const handleClearFilters = () => {
    setAgentId( '' );
    setAgentName( '' );
    setConstellationName( '' );
    setConstellationVersion( '' );
    setClientId( '' );
    setModelId( '' );
    setCorrelationId( '' );
    setRequestId( '' );
    setSessionIdFilter( '' );
    setDateFrom( '' );
    setDateTo( '' );
    setSortOrder( DEFAULT_SORT_ORDER );
    setSortBy( '' );
    setCurrentPage( 1 );
    setSessionId( '' );
    setSelectedId( '' );
    setLogData( { type: 'none', data: {} } );
    if ( logType === 'trace' ) {
      traceExtraStateRef.current = { traceKeyword: '', traceOffset: 0, selectedRequest: '' };
      setTraceMountKey( k => k + 1 );
    }
    writeUrl( {
      agentId: '', agentName: '', constellationName: '', constellationVersion: '',
      clientId: '', modelId: '', correlationId: '', requestId: '', sessionIdFilter: '',
      dateFrom: '', dateTo: '', sortOrder: DEFAULT_SORT_ORDER, sortBy: '',
      currentPage: 1, selectedId: ''
    } );
  };

  const toggleFilters = () => {
    const next = !filtersOpen;
    setFiltersOpen( next );
    writeUrl( { filtersOpen: next } );
  };

  const handleIncrement = () => {
    if ( !hasMorePages ) return;
    const nextPage = currentPage + 1;
    setCurrentPage( nextPage );
    setSelectedId( '' );
    writeUrl( { currentPage: nextPage, selectedId: '' } );
    fetchLogData( nextPage );
  };

  const handleDecrement = () => {
    if ( currentPage === 1 ) return;
    const prevPage = currentPage - 1;
    setCurrentPage( prevPage );
    setSelectedId( '' );
    writeUrl( { currentPage: prevPage, selectedId: '' } );
    fetchLogData( prevPage );
  };

  const handleSelectionChange = ( id: string ) => {
    setSelectedId( id );
    writeUrl( { selectedId: id } );
  };

  /** AgentTraceViewer reports trace-specific state changes (keyword, pagination,
   *  selection). Filter values are owned by /logs and not reflected here. */
  const handleTraceUrlStateChange = ( state: TraceUrlState ) => {
    traceExtraStateRef.current = {
      traceKeyword: state.traceKeyword,
      traceOffset: state.traceOffset,
      selectedRequest: state.selectedRequest
    };
    writeUrl();
  };

  /** Compose the initial state the AgentTraceViewer mounts with, from /logs
   *  filter state and the trace-only extras held in the ref. */
  const buildTraceInitialState = (): TraceUrlState => ( {
    traceAgentName: agentName,
    traceConstellationName: constellationName,
    traceConstellationVersion: constellationVersion,
    traceClientId: clientId,
    traceSessionId: sessionIdFilter,
    traceCorrelationId: correlationId,
    traceRequestId: requestId,
    traceDateFrom: dateFrom,
    traceDateTo: dateTo,
    traceSortOrder: sortOrder !== DEFAULT_SORT_ORDER ? sortOrder : '',
    traceSortBy: sortBy,
    traceKeyword: traceExtraStateRef.current.traceKeyword,
    traceOffset: traceExtraStateRef.current.traceOffset,
    selectedRequest: traceExtraStateRef.current.selectedRequest
  } );

  /** Build a filters object from the current state, ready for the API layer. */
  const buildFilters = ( page: number, includeSession: boolean ): ModelLogFilters => {
    const offset_num = ( page - 1 ) * 10;
    return {
      agent_id: agentId || undefined,
      agent_name: agentName || undefined,
      constellation_name: constellationName || undefined,
      constellation_version: constellationVersion || undefined,
      client_id: clientId || undefined,
      model_id: modelId || undefined,
      correlation_id: correlationId || undefined,
      request_id: requestId || undefined,
      session_id: ( sessionIdFilter || ( includeSession ? sessionId : '' ) ) || undefined,
      from_start_time: dateInputToIso( dateFrom ),
      to_end_time: dateInputToIso( dateTo ),
      sort_order: sortOrder,
      sort_by: sortBy || undefined,
      batch_size: 10,
      offset: offset_num
    };
  };

  const fetchLogData = async( page: number ) => {
    try {
      setIsLoading( true );
      // Page > 1 uses the cursor returned from the previous request when no
      // explicit session filter is set.
      const includeSession = page > 1 && !sessionIdFilter;

      await match( logType )
        .with( 'actionLog', async() => {
          const filters: LogFilters = buildFilters( page, includeSession );
          const res = await APIGateway.logs.actionLogsGet( filters );
          setLogData( { type: 'actionLog', data: res } );
          setSessionId( res.session_id );
          setHasMorePages( res.agent_actions.length === 10 );
        } )
        .with( 'modelLog', async() => {
          const filters: ModelLogFilters = buildFilters( page, includeSession );
          const res = await APIGateway.logs.modelLogsGet( filters );
          setLogData( { type: 'modelLog', data: res } );
          setSessionId( res.session_id );
          setHasMorePages( res.model_invocations.length === 10 );
        } )
        .with( 'requestResponseLog', async() => {
          const filters: LogFilters = buildFilters( page, includeSession );
          const res = await APIGateway.logs.requestLogsGet( filters );
          setLogData( { type: 'requestResponseLog', data: res } );
          setSessionId( res.session_id );
          setHasMorePages( res.completion_requests.length === 10 );
        } )
        .with( 'trace', async() => {
          // Trace view is handled by the AgentTraceViewer component directly
        } )
        .exhaustive();
    } catch( e ) {
      console.error( e );
    } finally {
      setIsLoading( false );
    }
  };

  const sortByOptions = SORT_BY_OPTIONS[logType];
  const sortByLabel = sortByOptions.find( o => o.value === sortBy )?.label
    ?? `${ sortByOptions[0]?.label ?? '' } (default)`;

  const hasActiveAdvancedFilter = !!(
    agentName || constellationName || constellationVersion || clientId
    || ( logType === 'modelLog' && modelId )
    || sessionIdFilter || dateFrom || dateTo || sortBy || sortOrder !== DEFAULT_SORT_ORDER
  );

  return (
    <div className="page">
      <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
        <span className='info_name'>
          <Heading as="h2">Log Viewer</Heading>
        </span>
      </div>
      <div className={ styles.toolbar }>
        <Input type="text" label="Agent ID" placeholder="Agent ID"
          value={ agentId }
          onChange={ ( e ) => { setAgentId( e.target.value ); setSessionId( '' ); } }
        />
        <Input type="text" label="Request ID" placeholder="Request ID"
          value={ requestId }
          onChange={ ( e ) => { setRequestId( e.target.value ); setSessionId( '' ); } }
        />
        <Input
          type="text"
          label="Correlation ID"
          placeholder='Correlation ID'
          value={ correlationId }
          onChange={ ( e ) => { setCorrelationId( e.target.value ); setSessionId( '' ); } }
        />
        <Menu
          label="Log type"
          value={ LOG_TYPE_LABELS[logType] }
          variant="select"
        >
          <MenuItem value="Model Log" onClick={ () => handleLogTypeChange( 'modelLog' ) } />
          <MenuItem value="Request/Response" onClick={ () => handleLogTypeChange( 'requestResponseLog' ) } />
          <MenuItem value="Action Log" onClick={ () => handleLogTypeChange( 'actionLog' ) } />
          <MenuItem value="Traces" onClick={ () => handleLogTypeChange( 'trace' ) } />
        </Menu>
        <Button
          variant='secondary'
          onClick={ toggleFilters }
          icon={ <Icon icon={ filtersOpen ? FiChevronUp : FiChevronDown } size="sm" /> }
        >
          {filtersOpen ? 'Hide filters' : 'More filters'}
          {hasActiveAdvancedFilter && !filtersOpen ? <span className={ styles.filterBadge } /> : null}
        </Button>
        {logType !== 'trace' && (
          <div className={ styles.pagination }>
            <a
              onClick={ handleDecrement }
              className={ clsx( currentPage === 1 && styles.disabled ) }
              style={ {
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1
              } }
            >
              <Icon icon={ FiChevronLeft } size="lg" />
            </a>
            <span style={ { margin: '0 10px' } }>{currentPage}</span>
            <a
              onClick={ handleIncrement }
              className={ clsx( !hasMorePages && styles.disabled ) }
              style={ {
                cursor: !hasMorePages ? 'not-allowed' : 'pointer',
                opacity: !hasMorePages ? 0.5 : 1
              } }
            >
              <Icon icon={ FiChevronRight } size="lg" />
            </a>
          </div>
        )}
        <Button variant='secondary' onClick={ handleSearch }>
          Search
        </Button>
      </div>
      {filtersOpen && (
        <div className={ styles.filtersPanel }>
          <div className={ styles.filtersGrid }>
            <Input type="text" label="Agent name" placeholder="e.g. data-explorer"
              value={ agentName }
              onChange={ ( e ) => { setAgentName( e.target.value ); setSessionId( '' ); } }
            />
            <Input type="text" label="Constellation name" placeholder="Constellation name"
              value={ constellationName }
              onChange={ ( e ) => { setConstellationName( e.target.value ); setSessionId( '' ); } }
            />
            <Input type="text" label="Constellation version" placeholder="e.g. 1.0.0"
              value={ constellationVersion }
              onChange={ ( e ) => { setConstellationVersion( e.target.value ); setSessionId( '' ); } }
            />
            <Input type="text" label="Client ID" placeholder="e.g. aidp-studio"
              value={ clientId }
              onChange={ ( e ) => { setClientId( e.target.value ); setSessionId( '' ); } }
            />
            {logType === 'modelLog' && (
              <Input type="text" label="Model ID" placeholder="Model ID"
                value={ modelId }
                onChange={ ( e ) => { setModelId( e.target.value ); setSessionId( '' ); } }
              />
            )}
            <Input type="text" label="Session ID" placeholder="Session ID"
              value={ sessionIdFilter }
              onChange={ ( e ) => { setSessionIdFilter( e.target.value ); setSessionId( '' ); } }
            />
            <Input type="datetime-local" label="From"
              value={ dateFrom }
              onChange={ ( e ) => { setDateFrom( e.target.value ); setSessionId( '' ); } }
            />
            <Input type="datetime-local" label="To"
              value={ dateTo }
              onChange={ ( e ) => { setDateTo( e.target.value ); setSessionId( '' ); } }
            />
            <Menu label="Sort by" value={ sortByLabel } variant="select">
              {sortByOptions.map( opt => (
                <MenuItem
                  key={ opt.value }
                  value={ opt.label }
                  onClick={ () => { setSortBy( opt.value ); writeUrl( { sortBy: opt.value } ); } }
                />
              ) )}
            </Menu>
            <Menu label="Sort order" value={ SORT_ORDER_LABELS[sortOrder] } variant="select">
              <MenuItem value={ SORT_ORDER_LABELS.DESC }
                onClick={ () => { setSortOrder( 'DESC' ); writeUrl( { sortOrder: 'DESC' } ); } }
              />
              <MenuItem value={ SORT_ORDER_LABELS.ASC }
                onClick={ () => { setSortOrder( 'ASC' ); writeUrl( { sortOrder: 'ASC' } ); } }
              />
            </Menu>
          </div>
          <div className={ styles.filtersActions }>
            <Button variant='secondary' onClick={ handleClearFilters }>
              <Icon icon={ FiX } size="sm" /> Clear all filters
            </Button>
          </div>
        </div>
      )}
      <div className="body">
        {logType === 'trace' ? (
          <AgentTraceViewer
            key={ traceMountKey }
            agentId={ agentId }
            useLocalZone={ useLocalZone }
            initialUrlState={ buildTraceInitialState() }
            onUrlStateChange={ handleTraceUrlStateChange }
            hideFiltersPanel
          />
        ) : isLoading ? (
          <div className={ styles.loader }>
            <Loader />
          </div>
        ) : (
          <>
            {logData.type === 'actionLog' && (
              <ActionLogViewer
                agentId={ agentId }
                requestId={ requestId }
                correlactionId={ correlationId }
                data={ logData.data.agent_actions }
                initialSelectedId={ selectedId }
                onSelectionChange={ handleSelectionChange }
              />
            )}
            {logData.type === 'modelLog' && (
              <ModelLogViewer
                agentId={ agentId }
                requestId={ requestId }
                correlactionId={ correlationId }
                data={ logData.data.model_invocations }
                initialSelectedId={ selectedId }
                onSelectionChange={ handleSelectionChange }
              />
            )}
            {logData.type === 'requestResponseLog' && (
              <RequestResponseLogViewer
                agentId={ agentId }
                requestId={ requestId }
                correlationId={ correlationId }
                data={ logData.data.completion_requests }
                initialSelectedId={ selectedId }
                onSelectionChange={ handleSelectionChange }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
